import { ICallTrackingNumbers, IRentsyncProperties } from '@llws/lift-entity-interfaces';
import { ActionContext } from 'vuex';
import dayjs from 'dayjs';
import {
  Equal,
  GreaterThanOrEqual, IQueryCommandOptions, IWhereCommand, In, Not, convertQueryCommandToQueryString,
} from '@llws/dynamic-router-utils';
import {
  IFilterPayloadV2,
  IExportFilterPayloadV2,
} from '@/shared';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { ICallTrackingNumbersState } from '@/shared/interfaces/call-tracking-numbers-store';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import { downloadCSV } from '@/shared/helpers/download-csv';
import { IForwardingNumbersStats } from '@/shared/interfaces/forwarding-number-stats';
import { TRootState } from '..';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';

// Base store configuration
const adminGatewayResourcePath: string = '/call-tracking-api/call_tracking_numbers';
const listViewName: string = '';
const showLogging: boolean = false;
const defaultGetListFilters: IQueryCommandOptions = {
  relations: [
    { path: 'callTrackingAssociations', alias: 'cta' },
    { path: 'cta.building', alias: 'b' },
    { path: 'cta.partner', alias: 'p' },
    { path: 'callTrackingForwardingMaps', alias: 'ctfm' },
  ],
};
const defaultGetSingleFilters: IQueryCommandOptions = {
  relations: [
    { path: 'callTrackingAssociations', alias: 'cta' },
    { path: 'cta.building', alias: 'b' },
    { path: 'cta.partner', alias: 'p' },
    { path: 'callTrackingForwardingMaps', alias: 'ctfm' },
  ],
};

let callTrackingFilters: IQueryCommandOptions = {};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<ICallTrackingNumbers>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

// Extend StoreBaseImplementation object to add custom state/getters/mutations/actions.
export default {
  namespaced: base.namespaced, // 'call-tracking-numbers/'

  state: {
    ...base.state,
    logStats: {
      countPhoneNumbers: 0,
      countLocalNumbers: 0,
      countTollFreeNumbers: 0,
    } as IForwardingNumbersStats,
  },

  getters: {
    ...base.getters,
    logStats(state: ICallTrackingNumbersState): IForwardingNumbersStats {
      return state.logStats!;
    },

  },

  mutations: {
    ...base.mutations,
    setLogStats: (state: ICallTrackingNumbersState, payload: IForwardingNumbersStats) => {
      state.logStats = payload;
    },
    updateRentsyncProperty: (
      state: ICallTrackingNumbersState,
      payload: { rp: IRentsyncProperties, ctnId: number, newRecord: boolean },
    ) => {
      const ctnIndex = (state.list?.data || []).findIndex((ctn) => ctn.id === payload.ctnId);
      if (ctnIndex !== -1 && payload.newRecord) {
        state.list!.data![ctnIndex].building!.rentsyncProperties = state.list!.data![ctnIndex].building!.rentsyncProperties!.concat(payload.rp);
      } else if (ctnIndex !== -1) {
        state.list!.data![ctnIndex].building!.rentsyncProperties!.find((rentsyncProperty) => {
          if (rentsyncProperty.id === payload.rp.id) {
            rentsyncProperty.state = payload.rp.state;
          }
          return rentsyncProperty.id === payload.rp.id;
        });
      }
    },

  },

  actions: {
    ...base.actions,

    async getCallTrackingData(ctx: ActionContext<ICallTrackingNumbersState, TRootState>, payload: IFilterPayloadV2) {
      const callTrackingNumberHasFilters = payload?.filters || {};
      if (payload.additional?.type === 'nonSyndicated') {
        callTrackingNumberHasFilters.relations = [
          { path: 'building', alias: 'building' },
          { path: 'building.city', alias: 'city' },
          { path: 'building.buildingsHasInteractiveVoiceResponses', alias: 'bhivr' },
          { path: 'bhivr.interactiveVoiceResponse', alias: 'ivr' },
          { path: 'building.suiteTypes', alias: 'st' },
          { path: 'callTrackingNumbersHasPartners', alias: 'ctnhp' },
          { path: 'ctnhp.partner', alias: 'p' },
          { path: 'building.rentsyncProperties', alias: 'rp' },
        ];
      } else {
        callTrackingNumberHasFilters.relations = [
          { path: 'building', alias: 'building' },
          { path: 'building.city', alias: 'city' },
          { path: 'building.buildingsHasInteractiveVoiceResponses', alias: 'bhivr' },
          { path: 'bhivr.interactiveVoiceResponse', alias: 'ivr' },
          { path: 'callTrackingNumbersHasPartners', alias: 'ctnhp' },
          { path: 'ctnhp.partner', alias: 'p' },
        ];
      }
      if (payload.additional?.type === 'details' || payload.additional?.type === 'activeNumberSummary') {
        callTrackingNumberHasFilters.relations = [...callTrackingNumberHasFilters.relations, { path: 'callGroup', alias: 'callGroup' }];
      }
      if (payload.additional?.type === 'activeNumberSummary') {
        // The group's entries tell the AI leasing agent apart from other call groups in the Forwarding column.
        callTrackingNumberHasFilters.relations = [
          ...callTrackingNumberHasFilters.relations,
          { path: 'callGroup.callGroupEntries', alias: 'callGroupEntries' },
        ];
        callTrackingNumberHasFilters.alias = 'ctn';
        callTrackingNumberHasFilters.select = ['countPhoneCalls', 'countMessages', 'sumSeconds'];
        callTrackingNumberHasFilters.aggregations = [
          {
            table: 'call_tracking_log_calls',
            propertyAlias: 'countPhoneCalls',
            whereCommands: [
              Equal('clientId', ctx.rootGetters['users/currentClient'].id),
              GreaterThanOrEqual('timestamp', payload.additional.startDate),
              Equal('dialedPhone', 'ctn.phoneNumber'),
            ],
            column: 'id',
            aggregate: 'COUNT',
          },
          {
            table: 'call_tracking_log_calls',
            propertyAlias: 'sumSeconds',
            whereCommands: [
              Equal('dialedPhone', 'ctn.phoneNumber'),
              Equal('clientId', ctx.rootGetters['users/currentClient'].id),
              GreaterThanOrEqual('timestamp', payload.additional.startDate),
            ],
            column: 'duration',
            aggregate: 'SUM',
          },
          {
            table: 'call_tracking_log_messages',
            propertyAlias: 'countMessages',
            whereCommands: [
              Equal('clientId', ctx.rootGetters['users/currentClient'].id),
              GreaterThanOrEqual('timestamp', payload.additional.startDate),
              Equal('dialedPhone', 'ctn.phoneNumber'),
            ],
            column: 'id',
            aggregate: 'COUNT',
          },
        ];
      }
      callTrackingFilters = callTrackingNumberHasFilters;
      const url = adminGatewayResourcePath + convertQueryCommandToQueryString(callTrackingNumberHasFilters);
      const response = await adminRequest.get(url);
      ctx.commit('set', response);
    },

    async getCallTrackingDataCSV(ctx: IAction<IStateStrict<ICallTrackingNumbers>>, payload: IExportFilterPayloadV2) {
      const url = adminGatewayResourcePath + convertQueryCommandToQueryString({ ...callTrackingFilters, limit: 999 });
      const response = await adminRequest.post(`${url}&csvExport=true`, { fields: payload.fields });
      downloadCSV(typeof response === 'string' ? response : '', payload.filename || `${dayjs().format('DD-MM-YYYY')}_report.csv`);
    },

    async getForwardingNumberStats(ctx: ActionContext<ICallTrackingNumbersState, TRootState>, payload: IFilterPayloadV2) {
      const statFilters = payload?.filters || {};
      statFilters.relations = callTrackingFilters.relations;
      let partnerKeys: string[] = [];
      if (statFilters.where) {
        partnerKeys = (statFilters.where.filter(
          (w) => (w as IWhereCommand).field === 'p.partnerKey',
        ) as IWhereCommand[])[0]?.value.split('|') || [];
      }
      statFilters.alias = 'cl';
      statFilters.select = ['countPhoneNumbers', 'countLocalNumbers', 'countTollFreeNumbers'];
      statFilters.aggregations = [
        {
          table: 'call_tracking_numbers',
          propertyAlias: 'countPhoneNumbers',
          whereCommands: [
            Equal('clientId', ctx.rootGetters['users/currentClient'].id),
            Not(Equal('pendingDelete', '1')),
          ],
          relations: [
            {
              path: 'building',
              alias: 'b',
            },
            { path: 'callTrackingNumbersHasPartners', alias: 'ctnhp' },
            { path: 'ctnhp.partner', alias: 'p' },
          ],
          column: 'id',
          aggregate: 'COUNT_DISTINCT',
        },
        {
          table: 'call_tracking_numbers',
          propertyAlias: 'countLocalNumbers',
          whereCommands: [
            Equal('clientId', ctx.rootGetters['users/currentClient'].id),
            Equal('isTollFree', '0'),
            Not(Equal('pendingDelete', '1')),
          ],
          relations: [
            {
              path: 'building',
              alias: 'b',
            },
            {
              path: 'callTrackingNumbersHasPartners',
              alias: 'ctnhp',
            },
            {
              path: 'ctnhp.partner',
              alias: 'p',
            },
          ],
          column: 'id',
          aggregate: 'COUNT_DISTINCT',
        },
        {
          table: 'call_tracking_numbers',
          propertyAlias: 'countTollFreeNumbers',
          whereCommands: [
            Equal('clientId', ctx.rootGetters['users/currentClient'].id),
            Equal('isTollFree', '1'),
            Not(Equal('pendingDelete', '1')),
          ],
          relations: [
            {
              path: 'building',
              alias: 'b',
            },
            { path: 'callTrackingNumbersHasPartners', alias: 'ctnhp' },
            { path: 'ctnhp.partner', alias: 'p' },
          ],
          column: 'id',
          aggregate: 'COUNT_DISTINCT',
        },
      ];
      if (partnerKeys.length) {
        const updatedAggregation = statFilters.aggregations.map((ac) => {
          ac.whereCommands!.push(
            In('p.partnerKey', partnerKeys),
          );
          return ac;
        });
        statFilters.aggregations = updatedAggregation;
      }
      const url = adminGatewayResourcePath + convertQueryCommandToQueryString(statFilters);
      const response = await adminRequest.get(url);
      const forwardingNumbersStatData: IForwardingNumbersStats = {
        countPhoneNumbers: response.data[0] ? response.data[0].countPhoneNumbers : 0,
        countLocalNumbers: response.data[0] ? response.data[0].countLocalNumbers : 0,
        countTollFreeNumbers: response.data[0] ? response.data[0].countTollFreeNumbers : 0,
      };
      ctx.commit('setLogStats', forwardingNumbersStatData);
    },

  },
};
